import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Trophy, ArrowRight, Sparkles, Building2 } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";
import { RouteTopBar } from "@/components/layout/RouteTopBar";

/**
 * Onboarding gate — FAN ONLY.
 * Corporate / Partner is intentionally NOT part of the public signup flow.
 * Enterprise access is approval-based via /corporate-access and SuperAdmin.
 */
export const Route = createFileRoute("/elegir-rol")({
  head: () => ({
    meta: [
      { title: "Elige tu experiencia · VerseSport" },
      { name: "description", content: "Selecciona cómo deseas vivir VerseSport: como fanático o como partner corporativo." },
      { property: "og:title", content: "Elige tu experiencia · VerseSport" },
      { property: "og:description", content: "Selecciona cómo deseas vivir VerseSport." },
    ],
  }),
  component: RoleSelectionPage,
});

const COPY = {
  ES: {
    eyebrow: "Bienvenido a VerseSport",
    title: "Empieza tu experiencia como fan",
    sub: "VerseSport para fanáticos: telemetría, IA, identidad y recompensas en vivo.",
    fanTitle: "Fanático",
    fanDesc: "Vive el partido con telemetría, IA, identidad de equipo y recompensas.",
    fanCta: "Entrar como Fan",
    corpNote: "¿Eres broadcaster, marca o sponsor?",
    corpLink: "Solicita acceso corporativo",
    footer: "El acceso enterprise requiere aprobación.",
  },
  EN: {
    eyebrow: "Welcome to VerseSport",
    title: "Start your fan experience",
    sub: "VerseSport for fans: live telemetry, AI, team identity and rewards.",
    fanTitle: "Fan",
    fanDesc: "Live the match with telemetry, AI, team identity and rewards.",
    fanCta: "Continue as Fan",
    corpNote: "Broadcaster, brand or sponsor?",
    corpLink: "Request corporate access",
    footer: "Enterprise access requires approval.",
  },
  PT: {
    eyebrow: "Bem-vindo ao VerseSport",
    title: "Comece sua experiência como fã",
    sub: "VerseSport para fãs: telemetria, IA, identidade do time e recompensas.",
    fanTitle: "Fanático",
    fanDesc: "Viva a partida com telemetria, IA, identidade do time e recompensas.",
    fanCta: "Entrar como Fã",
    corpNote: "Broadcaster, marca ou patrocinador?",
    corpLink: "Solicite acesso corporativo",
    footer: "O acesso enterprise requer aprovação.",
  },
};

function RoleSelectionPage() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const c = COPY[lang] ?? COPY.ES;

  useEffect(() => {
    void trackEvent("role_selection_viewed", {});
  }, []);

  const chooseFan = () => {
    void trackEvent("role_selection_chosen", { role: "fan" });
    try { window.localStorage.setItem("ss2026_role", "fan"); } catch { /* noop */ }
    void trackEvent("fan_onboarding_started", {});
    void navigate({ to: "/mi-identidad" });
  };

  return (
    <main className="min-h-screen bg-background">
      <RouteTopBar fallback="/welcome" />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-primary">
          {c.eyebrow}
        </p>
        <h1 className="mt-3 text-3xl font-black leading-tight sm:text-4xl">
          {c.title}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground sm:text-base">{c.sub}</p>

        <div className="mt-8 grid gap-4">
          <RoleCard
            icon={<Trophy className="h-7 w-7" />}
            title={c.fanTitle}
            desc={c.fanDesc}
            cta={c.fanCta}
            onClick={chooseFan}
            accent="primary"
          />
        </div>

        <div className="mt-6 rounded-xl border border-border/60 bg-accent/10 p-4">
          <p className="flex items-center gap-2 text-xs font-bold text-muted-foreground">
            <Building2 className="h-3.5 w-3.5" /> {c.corpNote}
          </p>
          <Link
            to="/corporate-access"
            onClick={() => void trackEvent("corporate_access_link_clicked", { surface: "elegir_rol" })}
            className="mt-1 inline-flex items-center gap-1 text-sm font-black text-primary hover:underline"
          >
            {c.corpLink} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <p className="mt-8 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" />
          {c.footer}
        </p>
      </div>
    </main>
  );
}

function RoleCard({
  icon, title, desc, cta, onClick, accent,
}: {
  icon: React.ReactNode; title: string; desc: string; cta: string;
  onClick: () => void; accent: "primary" | "muted";
}) {
  const isPrimary = accent === "primary";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative isolate flex flex-col items-start gap-3 overflow-hidden rounded-2xl border p-5 text-left transition hover:-translate-y-0.5 hover:shadow-xl ${
        isPrimary
          ? "border-primary/50 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent"
          : "border-border/60 bg-accent/10"
      }`}
    >
      <span
        className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${
          isPrimary ? "bg-primary text-primary-foreground" : "bg-foreground/10 text-foreground"
        }`}
        aria-hidden
      >
        {icon}
      </span>
      <div>
        <h2 className="text-lg font-black">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
      </div>
      <span
        className={`mt-auto inline-flex items-center gap-1.5 text-sm font-bold ${
          isPrimary ? "text-primary" : "text-foreground"
        }`}
      >
        {cta}
        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}